import React from 'react';
import styled from 'styled-components';

import { Tabs, Card, Modal, message } from 'antd';
import { AntdPageHeader } from '@yuhuinc/ui-library';

import TemplateList from './Templates/TemplateList';
import PackageList from './Packages/PackageList';
import ArchivedTemplates from './Templates/ArchivedTemplates';

import {
  getArchivedTemplates,
  getTemplates,
  getPackages,
  archiveTemplate,
  restoreTemplate,
  deleteTemplate,
  createTemplate,
  getArchivedPackages,
  archivePackage,
} from '../../landlord_app/application_templates/store/actions';

import { getTranslatedString } from 'helpers/i18n';

const StyledContainer = styled.div`
  padding: 0 24px;
`;

const ApplicationTemplates = ({ location }) => {
  const [templates, setTemplates] = React.useState([]);
  const [applicationPackages, setApplicationPackages] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const archiveApplicationTemplate = (template) => {
    Modal.confirm({
      title: `${getTranslatedString(
        'landlordPortal.applicationTemplate.modal.archivedTemplate.title'
      )}?`,
      content: getTranslatedString(
        'landlordPortal.applicationTemplate.modal.archivedTemplate.content'
      ),
      onOk: async () => {
        try {
          await archiveTemplate(template.id);
          loadTemplates('templates');
          message.success(
            getTranslatedString(
              'landlordPortal.applicationTemplate.messages.archiveTemplate.success'
            )
          );
        } catch (error) {
          message.error(
            getTranslatedString('landlordPortal.applicationTemplate.messages.archiveTemplate.error')
          );
        }
      },
      okText: getTranslatedString('okay'),
      cancelText: getTranslatedString('cancel'),
    });
  };

  const archiveApplicationPackage = (applicationPackage) => {
    Modal.confirm({
      title: getTranslatedString('landlordPortal.applicationPackages.modal.archivedPackage.title'),
      content: getTranslatedString(
        'landlordPortal.applicationPackages.modal.archivedPackage.content'
      ),
      onOk: async () => {
        try {
          await archivePackage(applicationPackage.id);
          loadTemplates('packages');
          message.success(
            getTranslatedString(
              'landlordPortal.applicationPackages.messages.archivedPackage.success'
            )
          );
        } catch (error) {
          message.error(
            getTranslatedString('landlordPortal.applicationPackages.messages.archivedPackage.error')
          );
        }
      },
      okText: getTranslatedString('okay'),
      cancelText: getTranslatedString('cancel'),
    });
  };

  const restoreApplicationTemplate = (template) => {
    restoreTemplate(template.id)
      .done(() => {
        loadTemplates('archived_templates');
        message.success(
          getTranslatedString('landlordPortal.applicationTemplate.messages.restoreTemplate.success')
        );
      })
      .catch((error) =>
        message.error(
          getTranslatedString('landlordPortal.applicationTemplate.messages.restoreTemplate.error')
        )
      );
  };

  const deleteApplicationTemplate = (template) => {
    Modal.confirm({
      title: `${getTranslatedString(
        'landlordPortal.applicationTemplate.modal.deleteTemplate.title'
      )}?`,
      content: getTranslatedString(
        'landlordPortal.applicationTemplate.modal.deleteTemplate.content'
      ),
      onOk: async () => {
        try {
          await deleteTemplate(template.id);
          loadTemplates('archived_templates');
          message.success(
            getTranslatedString(
              'landlordPortal.applicationTemplate.messages.deleteTemplate.success'
            )
          );
        } catch (error) {
          console.log(error);
          message.error(
            getTranslatedString('landlordPortal.applicationTemplate.messages.deleteTemplate.error')
          );
        }
      },
      okType: 'danger',
      okText: getTranslatedString('yes'),
      cancelText: getTranslatedString('cancel'),
    });
  };

  const copyApplicationTemplate = (template) => {
    Modal.confirm({
      title: getTranslatedString('landlordPortal.applicationTemplate.modal.copyTemplate.title'),
      content: `${getTranslatedString(
        'landlordPortal.applicationTemplate.modal.copyTemplate.content'
      )}?`,
      onOk: async () => {
        try {
          await createTemplate({ blueprint_template_id: template.id, commit: 'COPY_TEMPLATE' });
          loadTemplates('templates');
          message.success(
            getTranslatedString('landlordPortal.applicationTemplate.messages.copyTemplate.success')
          );
        } catch (error) {
          message.error(
            getTranslatedString('landlordPortal.applicationTemplate.messages.copyTemplate.error')
          );
        }
      },
      okText: getTranslatedString('yes'),
      cancelText: getTranslatedString('cancel'),
    });
  };

  const tabList = [
    {
      key: 'templates',
      title: getTranslatedString('landlordPortal.applicationTemplate.tabs.Templates'),
      content: (
        <TemplateList
          templates={templates}
          loading={loading}
          archiveApplicationTemplate={archiveApplicationTemplate}
          copyApplicationTemplate={copyApplicationTemplate}
        />
      ),
    },
    {
      key: 'packages',
      title: getTranslatedString('landlordPortal.applicationTemplate.tabs.Packages'),
      content: (
        <PackageList
          applicationPackages={applicationPackages}
          loading={loading}
          archiveApplicationPackage={archiveApplicationPackage}
        />
      ),
    },
    {
      key: 'archived_templates',
      title: getTranslatedString('landlordPortal.applicationTemplate.tabs.ArchivedTemplate'),
      content: (
        <ArchivedTemplates
          templates={templates}
          loading={loading}
          restoreApplicationTemplate={restoreApplicationTemplate}
          deleteApplicationTemplate={deleteApplicationTemplate}
        />
      ),
    },
    {
      key: 'archived_packages',
      title: getTranslatedString('landlordPortal.applicationTemplate.tabs.ArchivedPackages'),
      content: (
        <PackageList
          applicationPackages={applicationPackages}
          loading={loading}
          archiveApplicationPackage={archiveApplicationPackage}
        />
      ),
    },
  ];

  const loadTemplates = async (activeKey) => {
    setLoading(true);
    let response = null;

    if (activeKey === 'templates') {
      response = await getTemplates();
      setTemplates(response.application_templates);
    } else if (activeKey === 'packages') {
      response = await getPackages();
      setApplicationPackages(response.application_packages);
    } else if (activeKey === 'archived_templates') {
      response = await getArchivedTemplates();
      setTemplates(response.application_templates);
    } else if (activeKey === 'archived_packages') {
      response = await getArchivedPackages();
      setApplicationPackages(response.application_packages);
    }

    setLoading(false);
  };

  React.useEffect(() => {
    if (location.query.flashMessage) {
      message.success(location.query.flashMessage);
    }
    loadTemplates('templates');
  }, []);

  return (
    <div className="body" role="main">
      <AntdPageHeader
        onBack={() => window.history.back()}
        title={getTranslatedString('landlordPortal.applicationTemplate')}
      />

      <StyledContainer>
        <Tabs defaultActiveKey="templates" onTabClick={loadTemplates}>
          {tabList.map((tab) => (
            <Tabs.TabPane tab={tab.title} key={tab.key}>
              <Card>{tab.content}</Card>
            </Tabs.TabPane>
          ))}
        </Tabs>
      </StyledContainer>
    </div>
  );
};

export default ApplicationTemplates;
